"use client";

import Link from "next/link";
import { useMemo } from "react";

import type { SupabaseClient } from "@supabase/supabase-js";

import { computeInventoryHealthScore, type InventoryHealthMedInput } from "@/lib/inventory/computeInventoryHealthScore";
import { PLANS, planSupports } from "@/lib/plans/planConfig";

type MedRow = InventoryHealthMedInput & {
  id: string | number;
  name?: string;
  supplierName?: string | null;
};

type DashboardInsightHubProps = {
  meds: MedRow[];
  userId: string | null;
  supabaseReady: boolean;
  supabaseClient: SupabaseClient | null;
  /** Normalized plan: `free` | `pro` | `team` (see `normalizeRuntimePlan`). */
  runtimePlan: (typeof PLANS)[keyof typeof PLANS];
  isFree: boolean;
};

type InventoryHealthDockProps = {
  meds: MedRow[];
  profileKey?: string | null;
};

/**
 * Compact dashboard entry point: keep the health ring visible, open full intelligence in Insights.
 */
const HEALTH_COPY_POOLS = {
  p0: [
    "Your shelf is winning. You're losing!",
    "Your pantry is on its own.",
    "A few things are living on borrowed time.",
    "Your shelf has secrets. Find them.",
    "Mystery expiries incoming. Watch out.",
    "Things are expiring. You're not watching.",
    "Your dates are running wild.",
    "Your shelf is basically unsupervised.",
    "Expiry dates: 1. You: 0.",
    "Out there, things are expiring.",
    "Your shelf called. It needs help.",
    "Red zone. Time to act.",
    "Unknown expiries are dangerous.",
    "Lots still hiding from you.",
    "Things are expiring quietly.",
    "Stuff is expiring without a witness.",
    "Your pantry is off the grid.",
    "Blind spots everywhere. Let's fix that.",
    "Your items are unsupervised.",
    "The shelf is outpacing you.",
    "Mystery expiries everywhere.",
    "Your dates are running free.",
    "Expiry dates are winning. Not for long.",
    "Your shelf is in the wild.",
    "Things are expiring unseen.",
    "Invisible expiries are the worst kind.",
    "A lot is happening without you.",
    "Expiry dates don't announce themselves.",
    "Your items are off-book.",
    "Things are slipping by unseen.",
    "Some things are expiring today.",
    "Expiry chaos. Let's sort it.",
    "Flying blind. Time to land.",
    "Untracked and risky.",
    "Expiry dates: undefeated. For now.",
    "Your shelf is a mystery.",
    "Lots to uncover. Let's start.",
    "Your fridge knows more than you do.",
    "The shelf has the upper hand.",
    "Your items are living on the edge.",
    "Expiry amnesia. Classic.",
    "You're flying blind right now.",
    "Expiry dates: winning. You: not yet.",
    "Lots still unaccounted for.",
    "Some serious blind spots here.",
    "Things are expiring. Are you watching?",
    "The unknown expiries are the worst.",
    "Items expiring in silence.",
    "Expiry dates are ahead. Close the gap.",
    "Your shelf is off the radar.",
  ],
  p1: [
    "We all start somewhere.",
    "The journey starts here.",
    "First step done. What's next?",
    "You've peeked. Now dig in.",
    "Planted the seed. Water it.",
    "Starting is winning.",
    "First things tracked. More to come.",
    "You've started. Don't stop.",
    "Nice start. Keep adding.",
    "Awareness: growing.",
    "Good first move.",
    "A beginning is everything.",
    "First step taken. Breathe.",
    "Every item added counts.",
    "First steps. Big deal.",
    "You've begun. That's real.",
    "Tiny actions, big impact later.",
    "You're paying attention now.",
    "You've broken the seal. Keep going.",
    "The first add is always the hardest.",
    "Progress: unlocked.",
    "First items in. More to go.",
    "You started. The hard part's done.",
    "You're tuning in.",
    "Awareness: switched on.",
    "You've made your first move.",
    "Started. That's everything.",
    "Early mover. Good instinct.",
    "The ball is rolling.",
    "Ground floor. Only way is up.",
    "One at a time. That's how it starts.",
    "This is how it starts.",
    "You've opened the door. Walk in.",
    "You're tuning into your shelf.",
    "First step done. Breathe.",
    "The start of something good.",
    "The foundation stone is laid.",
    "Good instinct. Keep going.",
    "Slowly taking control.",
    "You've clocked in. Now track.",
    "Your tracking era begins.",
    "Starting line: crossed.",
    "Something started. Don't stop.",
    "Awareness: activated.",
    "Turning the light on.",
    "Starting is the whole game.",
    "You're building awareness.",
    "You've made a start. That's real.",
    "Entry point. Now go further.",
    "New habit forming. Hold it.",
  ],
  p2: [
    "Progress. We see you!",
    "Small wins. Keep going.",
    "You're in motion now.",
    "Warming up nicely.",
    "Getting warmer.",
    "Progress noted. Keep stacking.",
    "You're climbing.",
    "Something's clicking.",
    "Growing steadily.",
    "Habit seeds planted.",
    "You're in motion.",
    "Moving in the right direction.",
    "On the way up.",
    "You're getting traction.",
    "Progress is progress.",
    "Quarter of the way. Go!",
    "Look at you go.",
    "Gaining confidence.",
    "Early progress. Well done.",
    "Momentum: incoming.",
    "The map is forming.",
    "Slow and steady works.",
    "Rising steadily.",
    "Gaining your footing.",
    "On the scoreboard.",
    "Step by step.",
    "Forward motion. Good.",
    "Picking up speed.",
    "Steady as she goes.",
    "Traction: found.",
    "Something's shifting. Good.",
    "Getting sharper.",
    "Getting clearer.",
    "Nice early work.",
    "A habit is forming.",
    "Watch yourself climb.",
    "Building the habit.",
    "The seed is planted.",
    "Progress: confirmed.",
    "Steady progress. Good eye.",
    "Gathering pace.",
    "Better shape than last month.",
    "On the scoreboard. Keep scoring.",
    "Habit loading...",
    "Gaining confidence.",
    "The gap is closing.",
    "Step by step, you'll get there.",
    "Climbing with intention.",
    "The foundation is forming.",
    "Small steps. Big payoff.",
  ],
  p3: [
    "Building nicely!",
    "You're showing up.",
    "Consistency is forming.",
    "The habit is forming.",
    "You're building something real.",
    "Your system is taking shape.",
    "Momentum: loading...",
    "Good bones. Build on it.",
    "Shape is forming.",
    "Progressing well.",
    "The dots are connecting.",
    "Building block by block.",
    "Routine is forming. Nice.",
    "Tidy progress so far.",
    "Gaining ground steadily.",
    "The foundation is solid.",
    "Foundations first. Nailed it.",
    "Becoming a habit.",
    "You're getting organised.",
    "Getting a handle on it.",
    "Chipping away. Nicely.",
    "Getting cleaner.",
    "Framework: built.",
    "Look at that progress.",
    "Progress stacking up.",
    "More aware than yesterday.",
    "Patterns are forming.",
    "Routine is your friend now.",
    "Tracking is becoming a habit.",
    "Quietly building.",
    "Your system is waking up.",
    "More organised than yesterday.",
    "Building well.",
    "Shapes forming. Keep going.",
    "You're making this a thing.",
    "Stronger every time you check.",
    "Step by step, this gets easier.",
    "Growing steadily.",
    "Building beautifully.",
    "Awareness is rising.",
    "The engine's warming up.",
    "Making strides.",
    "This is getting real.",
    "The routine is sticking.",
    "A pattern is emerging.",
    "You're on the right path.",
    "Quietly killing it.",
    "Nice steady effort.",
    "Getting your rhythm.",
    "Growing stronger.",
  ],
  p4: [
    "Solid foundation. You're halfway there!",
    "Right in the middle. Push through.",
    "Half the battle won.",
    "Balance. Now tip it forward.",
    "Halfway home.",
    "Middle ground. Don't stay here.",
    "Half done. Half to go.",
    "In the middle and moving.",
    "Decent. Push higher.",
    "Looking even. Push forward.",
    "Halfway. Don't look back.",
    "Steady at the halfway.",
    "Solid midpoint.",
    "Even split. Go further.",
    "Halfway and holding.",
    "Right in the mix. Push on.",
    "The pivot point. Go up.",
    "At the middle, moving up.",
    "Even-keeled. Ready to tip?",
    "Even odds. Tip the scales.",
    "At the crossroads. Go forward.",
    "Midpoint. Stay the course.",
    "The tipping point is close.",
    "Middle of the pack. Not for long.",
    "Balanced. Ready to tip.",
    "Getting there. Don't stop.",
    "Right in the middle. Push up.",
    "Good work. More to do.",
    "Right at the midpoint. Up from here.",
    "The middle. Which way now?",
    "Midway there. Push on.",
    "Halfway and serious.",
    "The hinge point. Open up.",
    "Halfway — and hungry for more.",
    "Good progress. Tip the scale.",
    "Right at the threshold.",
    "Halfway and committed.",
    "The halfway badge is yours.",
    "Even. Now go further.",
    "Above or below? You choose.",
    "Even split. Power through.",
    "Midpoint energy. Keep it.",
    "Half the battle. Win the rest.",
    "Balanced. Tip it forward.",
    "The balance is shifting.",
    "Solid halfway mark.",
    "Halfway there. Eyes up.",
    "Right in the thick of it.",
    "The inflection point. Go up.",
    "Level ground. Now climb.",
  ],
  p5: [
    "Looking good. The momentum is building!",
    "More good than not.",
    "The scales are tipping. Nice.",
    "Looking better every check.",
    "Over the hump. Keep rolling.",
    "More covered than not. Good.",
    "On the right side of the line.",
    "Past halfway. Looking bright.",
    "Good side of average.",
    "Nicely above halfway.",
    "Control is creeping in.",
    "The momentum is yours now.",
    "Over halfway and rising.",
    "Comfort zone? Not here. Keep going.",
    "The majority is tracked. Keep it.",
    "Good work. Don't slow now.",
    "Past the middle. Feeling good.",
    "More done than not. Nice.",
    "Crossed the line. Now run.",
    "You're winning now.",
    "Green shoots. Keep growing.",
    "Well past halfway. Keep it.",
    "Better shape every time.",
    "Above average. Go further.",
    "Good side of halfway.",
    "Cruising above halfway.",
    "Your routine is working.",
    "Over the hump and rolling.",
    "Tipped the scales. Keep going.",
    "Clear majority: tracked.",
    "Majority controlled. Well done.",
    "Majority managed. Nice.",
    "Majority: yours.",
    "Better than average. Push further.",
    "The majority is handled.",
    "Looking after business.",
    "Momentum carries you now.",
    "You're in charge of most of it.",
    "Better than most.",
    "You're ahead of the game.",
    "Heading in the right direction.",
    "Progress, plain and simple.",
    "Over halfway. Great feeling.",
    "You own the majority now.",
    "Positive momentum. Ride it.",
    "The ship is turning.",
    "You're in positive territory.",
    "The good side of average.",
    "Control: incoming.",
    "Over halfway and accelerating.",
  ],
  p6: [
    "Steady and strong.",
    "You've got this figured out.",
    "Running a solid system.",
    "System: active. Keep it going.",
    "No wobbles. Just progress.",
    "Looking after your future self.",
    "Steady wins it.",
    "Consistent and calm.",
    "Solid. No cracks.",
    "Controlled and confident.",
    "Reliable and rising.",
    "Well on track.",
    "Running clean.",
    "Clearly committed.",
    "Smooth and consistent.",
    "Composed and consistent.",
    "No surprises at this level.",
    "Focused and forward.",
    "On track, on point.",
    "Efficient and effective.",
    "Composed and climbing.",
    "Nothing is slipping by.",
    "Organised and optimistic.",
    "Consistent. Calm. Capable.",
    "Dialled and dependable.",
    "Tuned in and tracking.",
    "Clear and controlled.",
    "Running strong.",
    "No mess. Just progress.",
    "Cool, calm, consistent.",
    "Confident and consistent.",
    "Sharp-eyed and steady.",
    "Organised and on it.",
    "Commanding the shelf.",
    "This is what organised feels like.",
    "Solid as they come.",
    "Locked and loaded.",
    "Well on the way.",
    "Tracking is second nature now.",
    "Clean habits, clean score.",
    "This is the life.",
    "Steady hand. Good score.",
    "Your shelf respects you.",
    "Tracking without thinking.",
    "Nothing falling through the cracks.",
    "Clear sailing now.",
    "Consistent. Calm. On track.",
    "Committed and composed.",
    "Organised. Calm. Winning.",
    "Organised and unstoppable.",
  ],
  p7: [
    "Great rhythm.",
    "Smooth sailing.",
    "In the zone.",
    "On point.",
    "Flowing well.",
    "Dialled in, almost.",
    "Groove: found.",
    "On a roll.",
    "Really good rhythm.",
    "Strong habits, strong score.",
    "Almost effortless now.",
    "Humming along.",
    "Reliable rhythm.",
    "Flowing and focused.",
    "Right where you want to be.",
    "In full stride.",
    "Singing the right tune.",
    "Great tempo.",
    "Clear-headed and consistent.",
    "Natural and fluid.",
    "Hitting your stride.",
    "A solid beat going.",
    "Finding your flow.",
    "Nailing it, mostly.",
    "Feels good, doesn't it.",
    "Smooth operator.",
    "Habit locked. Score rising.",
    "Fluid and focused.",
    "Everything is clicking.",
    "In your element.",
    "On a solid streak.",
    "Reliable rhythm.",
    "Moving well. Don't stop.",
    "On beat, on track.",
    "You've found your pace.",
    "Like clockwork.",
    "A well-oiled machine.",
    "The groove is real.",
    "This feels effortless.",
    "That rhythm suits you.",
    "Cruise control: on.",
    "Humming right along.",
    "Operating smoothly.",
    "In the sweet spot.",
    "Real rhythm, real results.",
    "Good groove, good score.",
    "Your shelf respects the process.",
    "Smooth, consistent, solid.",
    "Nailing the tempo.",
    "Locked in rhythm.",
  ],
  p8: [
    "Locked in. Sharp as ever.",
    "Tight and tidy.",
    "Crisp and controlled.",
    "Barely anything slips by.",
    "Polished and precise.",
    "Extremely sharp.",
    "Near the top. Stay there.",
    "Nearly nothing escapes you.",
    "Top tier stuff.",
    "Precision tracking.",
    "Sharp, tight, together.",
    "Seriously impressive.",
    "Barely anything misses you.",
    "Outstanding discipline.",
    "Tight grip on it all.",
    "The zone. You're in it.",
    "Near the summit.",
    "Practically flawless.",
    "Excellent control.",
    "Sharper than most.",
    "Exceptional control.",
    "Close to the top.",
    "Dominant tracking.",
    "Top shelf tracking.",
    "Almost nothing missed.",
    "Precision mode: on.",
    "Nothing sneaks past you now.",
    "Clinically sharp.",
    "Sharply maintained.",
    "High precision tracking.",
    "Immaculate effort.",
    "Near-perfect discipline.",
    "Tight and polished.",
    "Barely a thing escapes you.",
    "The shelf has met its match.",
    "On top of everything.",
    "Barely a crack in the armour.",
    "Practically perfect.",
    "Elite control.",
    "Iron grip. Well done.",
    "Virtually untouchable.",
    "The top is in sight.",
    "Essentially spotless.",
    "Tight, tidy, terrific.",
    "Almost nothing escapes you.",
    "Laser focused.",
    "Near flawless operation.",
    "Top-class tracking.",
    "This is elite tracking.",
    "Airtight operation.",
  ],
  p9: [
    "Flawless.",
    "Almost untouchable.",
    "Picture perfect. Almost.",
    "So close to perfect it stings.",
    "Virtually spotless.",
    "The gap is tiny. Close it.",
    "Razor sharp.",
    "Borderline perfect.",
    "Elite and almost effortless.",
    "The last few are hiding. Find them.",
    "Perfection is within reach.",
    "Just a sliver left. Grab it.",
    "Almost airtight.",
    "Nearly bulletproof.",
    "The finish line is right there.",
    "A hair's breadth from perfect.",
    "Squeaky clean.",
    "An inch from the top. Take it.",
    "All but perfect.",
    "So close. Don't blink.",
    "One last push.",
    "The final stretch.",
    "Nearly spotless.",
    "Virtually untouchable.",
    "One step from perfect.",
    "Spotless. Almost.",
    "Borderline immaculate.",
    "Nearly there. Finish strong.",
    "All but done.",
    "Nearly bulletproof.",
    "So close to spotless.",
    "Essentially flawless.",
    "One last thing. You know it.",
    "A sliver from perfection.",
    "Nearly perfect. You feel it.",
    "Just about perfect.",
    "Millimetres from the top.",
    "The finish is right there.",
    "Virtually nothing missed.",
    "A hair away. Grab it.",
    "Pinpoint precision.",
    "Final touch needed.",
    "The home stretch.",
    "Last piece of the puzzle.",
    "One inch from the top.",
    "Inch perfect. Almost.",
    "Almost airtight.",
    "Basically perfect.",
    "Almost nothing missed.",
    "Razor's edge. Take it.",
  ],
  p10: [
    "Nothing gets past you. Peak ExpiryDesk.",
    "Zero surprises. That's the goal.",
    "The standard. Full stop.",
    "Immaculate. Nothing wasted.",
    "Shelf mastery. Achieved.",
    "Untouchable. 100%.",
    "Nothing left to chance. Perfect.",
    "ExpiryDesk gold. You earned it.",
    "The whole shelf. Tracked. Done.",
    "Zero blind spots. That's mastery.",
    "Peak form. Every single item.",
    "A clean sweep. Brilliant.",
    "Airtight. Complete control.",
    "Total clarity. Nothing missed.",
    "Finished. Flawless. Done.",
    "Perfect tracking. No exceptions.",
    "The peak. Owned.",
    "Spotless record. You did it.",
    "Masterclass in tracking.",
    "Perfection. Full stop.",
    "Everything tracked. You win.",
    "ExpiryDesk perfection.",
    "100%. The only way to be.",
    "Everything accounted for. Legendary.",
    "Flawless run. That's ExpiryDesk.",
    "The cleanest run. Brilliant.",
    "The cleanest shelf wins. Congrats.",
    "Done. Clean. Perfect.",
    "Nothing left untracked. Wow.",
    "Shelf mastery. Truly earned.",
    "A full house. Nothing missed.",
    "The full picture. Every item.",
    "Total coverage. ExpiryDesk done right.",
    "Maximum control. Nothing wasted.",
    "The score says it all. 100%.",
    "Your shelf has never been so safe.",
    "Sealed tight. Not one thing missed.",
    "You tracked everything. That's rare.",
    "Nothing on your shelf is a secret. Perfect.",
    "Total domination. Your shelf bows.",
    "ExpiryDesk legend. Right here.",
    "Nothing past your radar. Clean sweep.",
    "Total mastery. ExpiryDesk proud.",
    "The full picture. Every single item.",
    "Total domination. 100% tracked.",
    "Spotless. Everything tracked.",
    "Locked down. Every item. Perfect.",
    "The cleanest shelf in the game.",
    "Your shelf. Fully under control.",
    "Nothing missed. Nothing wasted.",
  ],
} as const;

function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function shuffleDeterministic<T>(arr: readonly T[], seed: number): T[] {
  const out = [...arr];
  let s = seed || 1;
  for (let i = out.length - 1; i > 0; i -= 1) {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    const j = Math.abs(s) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function healthBandKey(score: number): keyof typeof HEALTH_COPY_POOLS {
  if (score <= 10) return "p0";
  if (score <= 20) return "p1";
  if (score <= 30) return "p2";
  if (score <= 40) return "p3";
  if (score <= 50) return "p4";
  if (score <= 60) return "p5";
  if (score <= 70) return "p6";
  if (score <= 80) return "p7";
  if (score <= 90) return "p8";
  if (score <= 99) return "p9";
  return "p10";
}

export function InventoryHealthDock({ meds, profileKey }: InventoryHealthDockProps) {
  const score = useMemo(() => computeInventoryHealthScore(meds), [meds]);
  const healthStatus = useMemo(() => {
    const band = healthBandKey(score.total);
    const pool = HEALTH_COPY_POOLS[band];
    const day = Math.floor(Date.now() / 86400000);
    const cycle = day % 50;
    const seed = hashString(`${profileKey || "guest"}:${band}`);
    const ordered = shuffleDeterministic(pool, seed);
    return ordered[cycle % ordered.length];
  }, [score.total, profileKey]);
  const tone = useMemo(() => {
    const pct = Math.max(0, Math.min(100, score.total)) / 100;
    const hue = 6 + pct * 156;
    // Keep status color informative but lower-key than neon.
    const ring = `hsl(${hue} 56% 42%)`;
    const text = `hsl(${hue} 42% 38%)`;
    // Visual ring accent is intentionally compact so high scores are not too loud.
    const accentPct = 8 + pct * 20; // 8% -> 28%
    return { ring, text, accentPct };
  }, [score.total]);

  return (
    <section className="ed-fade-in ed-health-dock" style={{ marginBottom: "0.45rem" }} aria-label="Inventory health quick access">
      <Link
        href="/insights#inventory-health"
        className="ed-health-dock__link"
        style={{
          display: "inline-flex",
          gap: "0.62rem",
          alignItems: "center",
          textDecoration: "none",
          padding: "0.2rem 0.1rem",
        }}
      >
        <div
          className="ed-health-dock__ring"
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `conic-gradient(${tone.ring} 0% ${tone.accentPct}%, var(--ed-glass-bg) ${tone.accentPct}% 100%)`,
            boxShadow: `0 6px 14px color-mix(in oklab, ${tone.ring} 24%, black 10%)`,
          }}
        >
          <div
            className="ed-health-dock__core"
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "var(--ed-bg-start)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="ed-health-dock__score"
              style={{
                fontSize: score.total >= 100 ? "0.76rem" : "0.84rem",
                fontWeight: 900,
                color: "var(--ed-text)",
                lineHeight: 1,
                letterSpacing: score.total >= 100 ? "-0.02em" : "-0.01em",
                whiteSpace: "nowrap",
                transform: "translateY(-0.5px)",
              }}
            >
              {score.total}%
            </div>
          </div>
        </div>
        <div className="ed-health-dock__copy" style={{ maxWidth: 240 }}>
          <p className="ed-health-dock__tagline" style={{ margin: 0, fontSize: "0.8rem", fontWeight: 700, color: tone.text, lineHeight: 1.3 }}>{healthStatus}</p>
        </div>
      </Link>
    </section>
  );
}

export function DashboardInsightHub({
  meds,
  userId,
  supabaseReady,
  supabaseClient,
  runtimePlan,
  isFree,
}: DashboardInsightHubProps) {
  const alertsOn = planSupports(runtimePlan, "expiryAlertsEnabled");

  const score = useMemo(() => computeInventoryHealthScore(meds), [meds]);

  return (
    <section
      className="ed-card-glass ed-fade-in ed-insight-hub"
      style={{
        marginBottom: "1rem",
        borderRadius: 16,
        padding: "1rem 1.05rem",
        border: "1px solid var(--ed-ui-info-border)",
        background: "linear-gradient(165deg, var(--ed-glass-bg) 0%, var(--ed-ui-info-bg) 100%)",
      }}
      aria-label="Inventory health score"
    >
      <header style={{ marginBottom: "0.85rem", paddingBottom: "0.85rem", borderBottom: "1px solid var(--ed-border)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.45rem 0.65rem", marginBottom: "0.35rem" }}>
          <span
            style={{
              fontSize: "0.62rem",
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ed-ui-info)",
              background: "var(--ed-ui-info-bg)",
              borderRadius: 999,
              padding: "0.2rem 0.5rem",
            }}
          >
            Preview
          </span>
          {runtimePlan === PLANS.TEAM ? (
            <span
              style={{
                fontSize: "0.62rem",
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--ed-ui-warning)",
                background: "var(--ed-ui-warning-bg)",
                borderRadius: 999,
                padding: "0.2rem 0.5rem",
              }}
            >
              Team · intelligence lane
            </span>
          ) : null}
        </div>
        <h2 style={{ margin: 0, fontSize: "1.08rem", fontWeight: 900, color: "var(--ed-text)", letterSpacing: "-0.025em", lineHeight: 1.2 }}>
          ExpiryDesk Intelligence Network™
        </h2>
        <p style={{ margin: "0.45rem 0 0", fontSize: "0.84rem", fontWeight: 700, color: "var(--ed-ui-info)", lineHeight: 1.45, maxWidth: "40rem" }}>
          ExpiryDesk doesn&apos;t just track your stock — it&apos;s built to learn from every scan, line, supplier tag, and correction until
          guidance compounds and competitors are arguing with averages.
        </p>
        <p style={{ margin: "0.45rem 0 0", fontSize: "0.78rem", color: "var(--ed-text-secondary)", lineHeight: 1.55, maxWidth: "44rem" }}>
          <strong style={{ color: "var(--ed-text-secondary)" }}>Next wave (privacy-first):</strong> cross-store benchmarks and model-backed expiry
          risk — shipped only when aggregate data is trustworthy, never as theatre numbers. Where digest-style summaries exist, they advance at
          most <strong style={{ color: "var(--ed-text)" }}>once per calendar day</strong> (on the date roll), not on every hover or tab focus.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 140px) 1fr",
          gap: "1rem",
          alignItems: "center",
        }}
      >
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: `conic-gradient(var(--ed-ui-info-strong) 0% ${score.total}%, var(--ed-glass-bg) ${score.total}% 100%)`,
              boxShadow: "0 8px 28px rgba(15, 23, 42, 0.12)",
            }}
          >
            <div
              style={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                background: "var(--ed-bg-start)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ fontSize: "1.65rem", fontWeight: 900, color: "var(--ed-text)", lineHeight: 1 }}>{score.total}</div>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, color: "var(--ed-text-secondary)", letterSpacing: "0.08em" }}>Health</div>
            </div>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: "var(--ed-text)", letterSpacing: "-0.02em" }}>
              Inventory Health Score
            </h2>
            <p style={{ margin: "0.35rem 0 0", fontSize: "0.8125rem", color: "var(--ed-text-secondary)", lineHeight: 1.55, fontWeight: 500 }}>
              {score.headline}
            </p>
            <dl
              style={{
                margin: "0.55rem 0 0",
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "0.35rem",
                fontSize: "0.72rem",
                color: "var(--ed-text-secondary)",
              }}
            >
              <div>
                <dt style={{ fontWeight: 700, color: "var(--ed-text-secondary)" }}>Expiry risk</dt>
                <dd style={{ margin: "0.15rem 0 0", fontWeight: 800, color: "var(--ed-text)" }}>{score.breakdown.expiryRisk}</dd>
              </div>
              <div>
                <dt style={{ fontWeight: 700, color: "var(--ed-text-secondary)" }}>Waste signal</dt>
                <dd style={{ margin: "0.15rem 0 0", fontWeight: 800, color: "var(--ed-text)" }}>{score.breakdown.wasteExposure}</dd>
              </div>
              <div>
                <dt style={{ fontWeight: 700, color: "var(--ed-text-secondary)" }}>Data coverage</dt>
                <dd style={{ margin: "0.15rem 0 0", fontWeight: 800, color: "var(--ed-text)" }}>{score.breakdown.dataCoverage}</dd>
              </div>
            </dl>
            {!alertsOn && isFree ? (
              <p style={{ margin: "0.55rem 0 0", fontSize: "0.72rem", color: "var(--ed-ui-warning)", fontWeight: 600 }}>
                Pro unlocks automated expiry digests and deeper risk signals —{" "}
                <Link href="/pricing?highlight=pro" style={{ color: "var(--ed-ui-info-strong)", fontWeight: 700 }}>
                  see plans
                </Link>
                .
              </p>
            ) : null}
          </div>
        </div>
    </section>
  );
}
